/**
 * SalesTaxReport - GST/HST Sales Tax Report for CRA filing
 * Shows tax collected vs input tax credits and net amount owing/refund.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { todayLocalIso } from '../../../utils/inputFormatters';
import { SalesTaxReport as SalesTaxReportData } from '../../../types/accounting';
import { accountingApi } from '../../../services/accountingApi';

interface SalesTaxReportProps {
  businessId: number;
  /** Business start date (YYYY-MM-DD); the default range runs from here to today */
  startDate?: string;
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
};

const SalesTaxReport: React.FC<SalesTaxReportProps> = ({ businessId, startDate: businessStart }) => {
  // Everything since the business started, so a purchase in an earlier quarter is never silently excluded
  const [startDate, setStartDate] = useState(businessStart || `${new Date().getFullYear()}-01-01`);
  const [endDate, setEndDate] = useState(todayLocalIso());
  const [report, setReport] = useState<SalesTaxReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await accountingApi.getSalesTaxReport(businessId, startDate, endDate);
      if (response.data) {
        setReport(response.data);
      } else {
        setError(response.message || 'Failed to generate report');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  }, [businessId, startDate, endDate]);

  // Show the numbers on open; the date pickers are for narrowing to a filing period
  useEffect(() => { generateReport(); }, [generateReport]);

  const isRefund = report ? report.net_tax < 0 : false;

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <h2 style={{ marginBottom: 4 }}>GST/HST Sales Tax Report</h2>
      <p style={{ color: '#6b7280', marginTop: 0, marginBottom: 24 }}>
        For CRA filing - Tax collected vs. input tax credits
      </p>

      {/* Date Range Picker */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 24, flexWrap: 'wrap' }}>
        <div>
          <label style={{ display: 'block', fontSize: 14, marginBottom: 4, fontWeight: 500 }}>Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 14, marginBottom: 4, fontWeight: 500 }}>End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
          />
        </div>
        <button
          className="btn-primary"
          onClick={generateReport}
          disabled={loading}
        >
          {loading ? 'Generating...' : 'Update'}
        </button>
      </div>

      {error && (
        <div style={{ padding: 12, backgroundColor: '#fef2f2', color: '#dc2626', borderRadius: 6, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {report && (
        <>
          {/* Section 1: Tax Collected */}
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, marginBottom: 20 }}>
            <h3 style={{ marginTop: 0, marginBottom: 12 }}>Tax Collected (on Sales)</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>Total Revenue (before tax)</span>
              <span style={{ fontWeight: 600 }}>{formatCurrency(report.tax_collected.total_revenue)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <span>Total Tax Collected</span>
              <span style={{ fontWeight: 600, color: '#059669' }}>{formatCurrency(report.tax_collected.total_tax)}</span>
            </div>

            {report.tax_collected.by_rate.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 13 }}>Tax Name</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontSize: 13 }}>Rate %</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontSize: 13 }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {report.tax_collected.by_rate.map((rate) => (
                    <tr key={rate.tax_rate_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '6px 8px', fontSize: 14 }}>{rate.tax_name}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px', fontSize: 14 }}>{rate.rate_percent}%</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px', fontSize: 14, color: '#059669' }}>
                        {formatCurrency(rate.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div style={{ fontSize: 13, color: '#6b7280' }}>
              Based on {report.tax_collected.invoice_count} invoice{report.tax_collected.invoice_count !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Section 2: Input Tax Credits */}
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, marginBottom: 20 }}>
            <h3 style={{ marginTop: 0, marginBottom: 12 }}>Input Tax Credits (Tax Paid on Expenses)</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>Purchases subject to tax (incl. capital items)</span>
              <span style={{ fontWeight: 600 }}>{formatCurrency(report.tax_paid.total_expenses)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <span>Total Tax Paid (ITCs)</span>
              <span style={{ fontWeight: 600, color: '#dc2626' }}>{formatCurrency(report.tax_paid.total_tax)}</span>
            </div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>
              Based on {report.tax_paid.entry_count} expense entr{report.tax_paid.entry_count !== 1 ? 'ies' : 'y'}
            </div>
          </div>

          {/* Section 3: Net Amount */}
          <div style={{
            border: '2px solid',
            borderColor: isRefund ? '#059669' : '#dc2626',
            borderRadius: 8,
            padding: 24,
            backgroundColor: isRefund ? '#f0fdf4' : '#fef2f2',
            marginBottom: 20
          }}>
            <h3 style={{ marginTop: 0, marginBottom: 16, textAlign: 'center' }}>Net GST/HST Summary</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>Tax Collected</span>
              <span style={{ fontWeight: 600, color: '#059669' }}>{formatCurrency(report.tax_collected.total_tax)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <span>Input Tax Credits</span>
              <span style={{ fontWeight: 600, color: '#dc2626' }}>-{formatCurrency(report.tax_paid.total_tax)}</span>
            </div>
            <hr style={{ border: 'none', borderTop: '2px solid #d1d5db', margin: '12px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 18, fontWeight: 700, color: isRefund ? '#059669' : '#dc2626' }}>
                {report.net_label}
              </span>
              <span style={{ fontSize: 22, fontWeight: 700, color: isRefund ? '#059669' : '#dc2626' }}>
                {formatCurrency(Math.abs(report.net_tax))}
              </span>
            </div>
          </div>

          {report.filing_note && (
            <p style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic', marginTop: 8 }}>
              {report.filing_note}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default SalesTaxReport;
